import React, { useState, useEffect, useCallback } from "react";
import apiClient from "../../../api/apiClient";
import Loading from "../../../components/Loading/Loading";
import InboundCampaignList from "./InboundCampaignList";
import InboundCampaignBuilder from "./InboundCampaignBuilder";
import CampaignAnalytics from "../../AdminPanel/tabs/CRM/campaign/CampaignAnalytics";
import "./InboundCampaignTab.css";

const SECTION_CONFIG = [
    { key: "tofu", label: "TOFU", sublabel: "Top of Funnel",    color: "#6E94B4", accentColor: "#3E7DAD" },
    { key: "mofu", label: "MOFU", sublabel: "Middle of Funnel", color: "#4E9490", accentColor: "#2D8080" },
    { key: "bofu", label: "BOFU", sublabel: "Bottom of Funnel", color: "#459E74", accentColor: "#2E8060" },
];

// Special sentinel value for "general / no segment" campaigns
const GENERAL_ID = "__general__";

const InboundCampaignTab = () => {
    const [segments, setSegments]               = useState({ tofu: [], mofu: [], bofu: [] });
    const [loadingSegments, setLoadingSegments] = useState(true);
    const [selectedId, setSelectedId]           = useState(null); // segment id or GENERAL_ID
    const [view, setView]                       = useState("list");
    const [selectedCampaignId, setSelectedCampaignId] = useState(null);
    
    const loadAllSegments = useCallback(async () => {
        setLoadingSegments(true);
        try {
            const [tofu, mofu, bofu] = await Promise.all([
                apiClient.get("/admin/crm/inbound/segments?funnelSection=tofu"),
                apiClient.get("/admin/crm/inbound/segments?funnelSection=mofu"),
                apiClient.get("/admin/crm/inbound/segments?funnelSection=bofu"),
            ]);
            setSegments({
                tofu: tofu.data.segments || [],
                mofu: mofu.data.segments || [],
                bofu: bofu.data.segments || [],
            });
        } catch {
            setSegments({ tofu: [], mofu: [], bofu: [] });
        } finally {
            setLoadingSegments(false);
        }
    }, []);

    useEffect(() => { loadAllSegments(); }, [loadAllSegments]);

    const allSegments = [
        ...segments.tofu.map(s => ({ ...s, section: "tofu" })),
        ...segments.mofu.map(s => ({ ...s, section: "mofu" })),
        ...segments.bofu.map(s => ({ ...s, section: "bofu" })),
    ];

    const selectedSegment = selectedId && selectedId !== GENERAL_ID
        ? allSegments.find(s => s.id === selectedId) || null
        : null;

    const isGeneral = selectedId === GENERAL_ID;

    const handleSelect = (id) => {
        setSelectedId(id);
        setView("list");
        setSelectedCampaignId(null);
    };

    const handleNew       = () => { setSelectedCampaignId(null); setView("builder"); };
    const handleEdit      = (id) => { setSelectedCampaignId(id); setView("builder"); };
    const handleAnalytics = (id) => { setSelectedCampaignId(id); setView("analytics"); };
    const handleBack      = () => { setView("list"); setSelectedCampaignId(null); };
    const handleSave      = () => { setView("list"); setSelectedCampaignId(null); };

    const sectionForId = (id) => SECTION_CONFIG.find(s => segments[s.key]?.some(seg => seg.id === id));
    const accentForSelected = selectedSegment ? sectionForId(selectedSegment.id)?.accentColor : null;

    return (
        <div className="icam-layout">

            {/* ── Sidebar ── */}
            <div className="icam-sidebar">
                <div className="icam-sidebar__top">
                    <span className="icam-sidebar__heading">Campaigns</span>
                </div>

                <div className="icam-sidebar__scroll">
                    {/* General / unsegmented */}
                    <div
                        className={`icam-general-item${isGeneral ? " icam-general-item--active" : ""}`}
                        onClick={() => handleSelect(GENERAL_ID)}
                    >
                        <svg className="icam-general-item__icon" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <circle cx="12" cy="12" r="10"/>
                            <line x1="2" y1="12" x2="22" y2="12"/>
                            <path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z"/>
                        </svg>
                        All Inbound
                    </div>

                    {loadingSegments ? (
                        <div style={{ padding: "var(--space-5)", display: "flex", justifyContent: "center" }}>
                            <Loading />
                        </div>
                    ) : (
                        SECTION_CONFIG.map((sec, si) => {
                            const segs = segments[sec.key] || [];
                            return (
                                <React.Fragment key={sec.key}>
                                    {si > 0 && <div className="icam-section-divider" />}
                                    <div className="icam-section-label">
                                        <span className="icam-section-label__dot" style={{ background: sec.color }} />
                                        {sec.label}
                                        <span className="icam-section-label__sub">— {sec.sublabel}</span>
                                    </div>

                                    {segs.length === 0 ? (
                                        <div className="icam-sidebar__none">No segments</div>
                                    ) : (
                                        segs.map(seg => (
                                            <div
                                                key={seg.id}
                                                className={`icam-seg-item${selectedId === seg.id ? " icam-seg-item--active" : ""}`}
                                                style={selectedId === seg.id ? { borderLeftColor: sec.accentColor } : {}}
                                                onClick={() => handleSelect(seg.id)}
                                            >
                                                <span className="icam-seg-item__name" title={seg.name}>{seg.name}</span>
                                                <span className="icam-seg-item__count">{seg.leadCount ?? 0}</span>
                                            </div>
                                        ))
                                    )}
                                </React.Fragment>
                            );
                        })
                    )}
                </div>
            </div>

            {/* ── Right panel ── */}
            <div className="icam-panel">
                {!selectedId ? (
                    <div className="icam-panel__placeholder">
                        <svg width="44" height="44" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" strokeLinejoin="round">
                            <path d="M14.5 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V7.5L14.5 2z"/>
                            <polyline points="14 2 14 8 20 8"/>
                            <line x1="16" y1="13" x2="8" y2="13"/>
                            <line x1="16" y1="17" x2="8" y2="17"/>
                        </svg>
                        Select a segment or "All Inbound"<br />to view and build campaigns.
                    </div>
                ) : view === "analytics" ? (
                    <div style={{ flex: 1, overflow: "auto", padding: "var(--space-5)" }}>
                        <CampaignAnalytics campaignId={selectedCampaignId} onBack={handleBack} />
                    </div>
                ) : view === "builder" ? (
                    <div style={{ flex: 1, overflow: "auto", padding: "var(--space-5)" }}>
                        <InboundCampaignBuilder
                            campaignId={selectedCampaignId}
                            segmentId={isGeneral ? null : selectedId}
                            segment={selectedSegment}
                            accentColor={accentForSelected}
                            onSave={handleSave}
                            onCancel={handleBack}
                        />
                    </div>
                ) : (
                    <InboundCampaignList
                        segmentId={isGeneral ? null : selectedId}
                        isGeneral={isGeneral}
                        segment={selectedSegment}
                        onNew={handleNew}
                        onEdit={handleEdit}
                        onAnalytics={handleAnalytics}
                    />
                )}
            </div>
        </div>
    );
};

export default InboundCampaignTab;
